import React, { useState, useRef, useEffect } from 'react';
import { StripePricesAndProducts } from '@/openapi';
import { IStripeProductPrice, getActiveProductsFromPricesProducts } from '@/utils/paymentUtils';
import { clsx } from 'clsx';
import { _ } from "@/pages/_app";
import { trackEvent } from '@/utils/analyticsFunctions';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import ButtonNew from './buttons/ButtonNew';
import TextInputNew from './inputs/TextInputNew';
import ChevronIcon from './icons/ChevronIcon';
import AppleIcon from './icons/AppleIcon';
import GooglePlayIcon from './icons/GooglePlayIcon';
import FlameIcon from './icons/FlameIcon';
import PlusIcon from './icons/PlusIcon';
import HorseDayIcon from './icons/HorseDayIcon';
import GiftcardIcon from './icons/GiftcardIcon';
import CheckmarkIcon from './icons/CheckmarkIcon';

interface PricingCardsProps {
  pricesProducts: StripePricesAndProducts;
  onSelectPlan: (product: IStripeProductPrice, buyAsGiftcard: boolean) => void;
  loading?: boolean;
  giftcard: string;
  setGiftcard: (value: string) => void;
  showRedeemGiftcard: boolean;
  setShowRedeemGiftcard: (value: boolean) => void;
  onRedeemGiftcard: () => void;
  loadingGiftcard: boolean;
}

const PricingCards = ({
  pricesProducts,
  onSelectPlan,
  loading,
  giftcard,
  setGiftcard,
  showRedeemGiftcard,
  setShowRedeemGiftcard,
  onRedeemGiftcard,
  loadingGiftcard
}: PricingCardsProps) => {
  const router = useRouter();
  const [isYearly, setIsYearly] = useState(true);
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({});

  const yearlyBtnRef = useRef<HTMLButtonElement>(null);
  const monthlyBtnRef = useRef<HTMLButtonElement>(null);
  const [sliderStyle, setSliderStyle] = useState({ width: 0, left: 0 });
  const [hasLoaded, setHasLoaded] = useState(false);

  // Track page view on mount
  useEffect(() => {
    trackEvent('Checkout - Page Viewed', {
      referrer: document.referrer,
      utm_source: router.query.utm_source as string,
      utm_campaign: router.query.utm_campaign as string,
      utm_medium: router.query.utm_medium as string,
      source: 'checkout-page'
    });
  }, []);

  // Track toggle changes
  const handleToggleChange = (yearly: boolean) => {
    setIsYearly(yearly);
    trackEvent('Checkout - Toggle Changed', {
      billing_cycle: yearly ? 'yearly' : 'monthly',
      source: 'checkout-page'
    });
  };

  useEffect(() => {
    const btn = isYearly ? yearlyBtnRef.current : monthlyBtnRef.current;
    if (btn) {
      setSliderStyle({
        width: btn.offsetWidth,
        left: btn.offsetLeft
      });
    }
  }, [isYearly]);

  useEffect(() => {
    const timer = setTimeout(() => setHasLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const toggleAccordion = (key: string) => {
    setOpenAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const allProducts = getActiveProductsFromPricesProducts(pricesProducts);

  const getProductByEntitlementAndDuration = (entitlement: string, duration: string) => {
    return allProducts.find(
      p => p.product.metadata.entitlement === entitlement &&
        p.product.metadata.duration === duration
    );
  };

  const silverYearly = getProductByEntitlementAndDuration('BROWSING', 'YEAR');
  const silverMonthly = getProductByEntitlementAndDuration('BROWSING', 'MONTH');
  const premiumYearly = getProductByEntitlementAndDuration('PRO', 'YEAR');
  const premiumMonthly = getProductByEntitlementAndDuration('PRO', 'MONTH');

  const plans = [
    {
      id: 'silver',
      name: _("checkout-plan_silver"),
      headerClass: 'bg-gradient-to-b from-[#faf0e9] to-[#ece3dd]',
      contentClass: 'bg-[#f8f2ee]',
      getProduct: () => isYearly ? silverYearly : silverMonthly,
      features: [
        {
          title: _("checkout-plan_competition-insights-title"),
          description: _("checkout-plan_competition-insights-description"),
        },
        {
          title: _("checkout-plan_audience-score-title"),
          description: _("checkout-plan_audience-score-description"),
        },
        {
          title: _("checkout-plan_worldfengur-browsing-title"),
          description: _("checkout-plan_worldfengur-browsing-description"),
        },
      ]
    },
    {
      id: 'premium',
      name: _("checkout-plan_premium"),
      headerClass: 'bg-gradient-to-b from-[#eaceb5] to-[#d6baa1]',
      contentClass: 'bg-[#f1decc]',
      getProduct: () => isYearly ? premiumYearly : premiumMonthly,
      showTag: false,
      extraNote: _("checkout-plan_extra-note"),
      features: [
        {
          title: _("checkout-plan_gait-and-training-title"),
          description: _("checkout-plan_gait-and-training-description"),
        },
        {
          title: _("checkout-plan_complete-care-title"),
          description: _("checkout-plan_complete-care-description"),
        },
        {
          title: _("checkout-plan_eidfaxi-videos-title"),
          description: _("checkout-plan_eidfaxi-videos-description"),
        },
      ]
    },
    {
      id: 'giftcard',
      name: _("checkout-plan_giftcard-subtitle"),
      headerClass: 'bg-gradient-to-b from-[#faf0e9] to-[#ece3dd]',
      contentClass: 'bg-[#f8f2ee]',
      priceText: _("checkout-plan_giftcard-title"),
      isGiftcard: true,
      giftcardNote: _("checkout-plan_giftcard-note"),
      showTag: false,
    }
  ];

  const handlePlanClick = (plan: typeof plans[0]) => {
    if (!plan.getProduct) return;

    const product = plan.getProduct();
    if (!product) {
      console.error('Product not found for plan:', plan.id, 'isYearly:', isYearly);
      return;
    }

    // Track plan selection
    trackEvent('Checkout - Plan Selected', {
      plan_id: plan.id,
      plan_name: plan.name,
      billing_cycle: isYearly ? 'yearly' : 'monthly',
      price: product.price.unitAmount,
      currency: 'EUR',
      utm_source: router.query.utm_source as string,
      utm_campaign: router.query.utm_campaign as string,
      utm_medium: router.query.utm_medium as string,
      source: 'checkout-page'
    });

    if (onSelectPlan) {
      onSelectPlan(product, false);
    }
  };

  const formatPrice = (amount: number | undefined) => {
    if (!amount) return '€0.00';
    return `€${(amount / 100).toFixed(2)}`;
  };

  const renderPlanCard = (plan: typeof plans[0]) => {
    const product = plan.getProduct?.();
    const price = product?.price.unitAmount;
    const displayPrice = price;

    return (
      <div className="flex flex-col rounded-[1.5rem] sm:rounded-[2.5rem] relative overflow-visible">
        <div className={clsx(plan.headerClass, "flex flex-col gap-[0.5rem] sm:gap-[0.8rem] rounded-t-[2rem] sm:rounded-t-[2.5rem] w-full px-6 sm:px-9 pt-[1.8rem] sm:pt-[2.5rem] pb-[3.6rem] sm:pb-[4.5rem] relative overflow-visible")}>
          <div className="flex flex-row justify-between items-center w-full">
            <h3 className="text-[1.2rem] sm:text-[1.5rem] leading-[1.5rem] sm:leading-[2rem] font-poppins font-normal">{plan.name}</h3>
            {plan.isGiftcard ? <GiftcardIcon /> : <HorseDayIcon />}
          </div>
          <div className="flex flex-row justify-start items-end gap-[0.3rem]">
            <p className="text-[#1b1817] text-[1.6rem] sm:text-[2rem] font-semibold leading-[2rem] tracking-[-0.22px] font-poppins">
              {plan.priceText ? plan.priceText : formatPrice(price)}
            </p>
            {!plan.priceText && (
              <p className="text-[1rem] sm:text-[1.1rem] font-normal font-poppins">
                /{isYearly ? _('year') : _('month')}
              </p>
            )}
          </div>
        </div>

        <div className={clsx(plan.contentClass, "flex flex-col rounded-[2.5rem] w-full p-6 sm:p-9 pt-[1rem] sm:pt-[1.6rem] relative -top-[2.5rem] overflow-hidden")}>
          {!plan.isGiftcard && plan.features?.map((feature, idx) => {
            const key = `${plan.id}-${idx}`;
            const isOpen = openAccordions[key];
            const borderColor = plan.id === 'premium' ? 'border-[#e7cbb2]' : 'border-[#ecded2]';

            return (
              <button
                key={idx}
                onClick={() => toggleAccordion(key)}
                className={clsx(
                  "cursor-pointer bg-transparent w-full py-[0.8rem] sm:py-[1rem]",
                  `border-b ${borderColor}`
                )}
              >
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row justify-start items-center gap-2">
                    <p className="text-[#1B1817] text-[1rem] sm:text-[1.1rem] leading-[1.3rem] sm:leading-[1.5rem] font-medium text-left">{feature.title}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <ChevronIcon className={`w-[28px] sm:w-[32px] h-[28px] sm:h-[32px] ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                <div className={clsx("overflow-hidden transition-all duration-300", isOpen ? 'max-h-40' : 'max-h-0')}>
                  <p className="text-[#42311f] text-[.9rem] sm:text-[.95rem] leading-[1.2rem] leading-[1.2rem] sm:leading-[1.4rem] text-left pt-[0.5rem] font-normal pr-[2.5rem]">{feature.description}</p>
                </div>
              </button>
            );
          })}

          {plan.extraNote && !plan.giftcardNote && (
            <div className="flex items-center gap-2 py-3 mt-2">
              <PlusIcon className="w-[20px] sm:w-[24px] h-[20px] sm:h-[24px]" />
              <p className="text-[#9a6c43] text-[1rem] sm:text-[1.1rem] leading-[1.3rem] sm:leading-[1.5rem] font-normal">{plan.extraNote}</p>
            </div>
          )}

          {plan.giftcardNote && plan.isGiftcard && (
            <div className="flex items-center justify-between gap-2 py-3 mb-3">
              <p className="text-[#1B1817] text-[1rem] sm:text-[1.1rem] leading-[1.3rem] sm:leading-[1.5rem] font-medium text-left">
                {plan.giftcardNote}
              </p>
              <CheckmarkIcon className="w-[26px] sm:w-[32px] h-[26px] sm:h-[32px]" />
            </div>
          )}

          <div className={clsx("w-full mt-2", !plan.isGiftcard && "mt-8")}>
            {plan.isGiftcard ? (
              <div className="flex flex-col gap-3">
                <ButtonNew
                  variant="primary"
                  onClick={() => {
                    const premiumProduct = premiumYearly;
                    if (premiumProduct && onSelectPlan) {
                      onSelectPlan(premiumProduct, true);
                    }
                  }}
                  disabled={loading || !premiumYearly}
                  className="w-full"
                >
                  {_('giftcard-buy_button')}
                </ButtonNew>

                {showRedeemGiftcard && (
                  <TextInputNew
                    variant="secondary"
                    value={giftcard}
                    placeholder="Enter your giftcard code"
                    onChange={(v) => setGiftcard(v)}
                    onEnter={onRedeemGiftcard}
                    className='mt-[1rem]'
                  />
                )}

                <ButtonNew
                  variant="giftcard--redeem"
                  onClick={() => {
                    if (!showRedeemGiftcard) {
                      setShowRedeemGiftcard(true);
                    } else {
                      onRedeemGiftcard();
                    }
                  }}
                  disabled={loadingGiftcard}
                  className="w-full"
                >
                  {loadingGiftcard ? _('redeeming') : _('giftcard-redeem_button')}
                </ButtonNew>
              </div>
            ) : (
              <ButtonNew
                variant={plan.id === 'premium' ? 'premium' : plan.id === 'silver' ? 'silver' : 'primary'}
                onClick={() => handlePlanClick(plan)}
                disabled={loading || !product}
                className="w-full"
              >
                {!product
                  ? _('not-available')
                  : plan.id === 'premium'
                    ? _('premium-subscribe_button')
                    : plan.id === 'silver'
                      ? _('silver-subscribe_button')
                      : plan.id === 'giftcard'
                        ? _('giftcard-buy_button')
                        : `Subscribe to ${plan.name.replace(' Plan', '').replace(' plan', '')}`
                }
              </ButtonNew>
            )}
          </div>
        </div>

        {plan.showTag && isYearly && (
          <div className="flex flex-row gap-[0.8rem] justify-start items-center absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#32281d] text-[#ecbe8e] rounded-[200px] px-4 sm:px-6 py-[0.5rem] sm:py-[0.65rem] text-[0.9rem] sm:text-[1.05rem] leading-[1rem] sm:leading-[1.3rem]">
            <p className="whitespace-nowrap">
              {_("checkout-plan_badge-text")}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="relative z-[100] bg-white lg:bg-[linear-gradient(180deg,#FFFFFF_0%,#F9F4EF_100%)] rounded-br-[2rem] sm:rounded-br-[4rem] rounded-bl-[2rem] sm:rounded-bl-[4rem] flex justify-center items-start pt-[1rem] pb-[2rem] sm:pb-[3.2rem] md:pb-[5.2rem] top-0">
      <div className="max-w-[1360px] px-5 w-full lg:px-10">
        <div className="flex flex-col justify-center items-center gap-[1.5rem] sm:gap-[3.2rem]">
          <div className="flex w-full flex-col justify-center items-center gap-[0.75rem] sm:gap-[1rem]">
            <h1 className="text-[#231e1d] text-center font-semibold text-[2.4rem] sm:text-[2.8rem] lg:text-[4.4rem] leading-[2.8rem] sm:leading-[3.2rem] lg:leading-[4.6rem] mb-0 font-poppins tracking-[-0.5px]">
              {_("checkout-hero_title")}
            </h1>
            <p className="text-[#3b2f2f] text-center max-w-[90%] font-[Poppins,sans-serif font-normal text-[1rem] sm:text-[1.1rem] lg:text-[1.2rem] leading-[1.6em] sm:leading-[1.7rem] lg:leading-[1.8]">
              {_("checkout-hero_subtitle")}
            </p>
          </div>

          <div className="flex flex-col w-full justify-center items-center gap-[1rem]">
            <div className="relative flex items-center gap-0 border-2 border-[#55402a] rounded-full p-[0.3rem] font-semibold text-[1rem] leading-[1.2rem] sm:text-[1.3rem] sm:leading-[1.5rem] font-poppins text-[#68431c] tracking-[0.22px] bg-gradient-to-b from-[rgba(160,101,40,0.05)] to-[rgba(160,101,40,0.1)]">
              <button
                ref={yearlyBtnRef}
                onClick={() => {
                  setIsYearly(true);
                  trackEvent('Checkout - Toggle Changed', {
                    billing_cycle: 'yearly',
                    source: 'checkout-page'
                  });
                }}
                className="z-[3] cursor-pointer bg-transparent px-[1rem] py-[0.5rem] sm:px-[1.8rem] sm:py-[0.9rem] relative"
              >
                {_("checkout-toggle_yearly")}
              </button>

              <button
                ref={monthlyBtnRef}
                onClick={() => {
                  setIsYearly(false);
                  trackEvent('Checkout - Toggle Changed', {
                    billing_cycle: 'monthly',
                    source: 'checkout-page'
                  });
                }}
                className="z-[3] cursor-pointer bg-transparent px-[1rem] py-[0.5rem] sm:px-[1.8rem] sm:py-[0.9rem] relative"
              >
                {_("checkout-toggle_monthly")}
              </button>
              <span
                className={`absolute bg-gradient-to-b from-[rgba(160,101,40,0.07)] to-[rgba(160,101,40,0.22)] border-2 border-[#55402a] rounded-full ${hasLoaded ? 'transition-all duration-300' : ''}`}
                style={{
                  width: `${sliderStyle.width}px`,
                  left: `${sliderStyle.left}px`,
                  top: '0.3rem',
                  height: 'calc(100% - 0.6rem)'
                }}
              />
            </div>
          </div>

          <div className="w-full mt-[2rem]">
            {/* DESKTOP */}
            <div className="hidden lg:flex flex-row gap-[1.5rem] xl:gap-[2.5rem] justify-center">
              {plans.map((plan) => {
                const mobileOrder = plan.id === 'premium' ? 'order-1' :
                  plan.id === 'silver' ? 'order-2' :
                    'order-3';

                return (
                  <div
                    key={plan.id}
                    className={clsx(
                      "w-full",
                      mobileOrder,
                      "md:order-none"
                    )}
                  >
                    {renderPlanCard(plan)}
                  </div>
                );
              })}
            </div>

            {/* MOBILE */}
            <div className="flex flex-col lg:hidden gap-[1rem] justify-center">
              {plans.map((plan) => {
                const mobileOrder = plan.id === 'premium' ? 'order-1' :
                  plan.id === 'silver' ? 'order-2' :
                    'order-3';

                return (
                  <div
                    key={plan.id}
                    className={clsx(
                      "w-full",
                      mobileOrder
                    )}
                  >
                    {renderPlanCard(plan)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingCards;
